import type { EntityId, WorldState } from "../../sim";
import { angleDelta } from "../../sim/math/angle";
import { wrappedDelta } from "../../sim/math/torus";

/** Corrections larger than this (world units) snap: respawns, teleports, big mispredictions. */
export const CORRECTION_SNAP_DISTANCE = 220;
/** Time constant (ms) of the fade: ~63 % of a correction is gone after this long. */
export const CORRECTION_FADE_MS = 100;
const MIN_OFFSET = 0.05;

export interface PoseOffset {
	x: number;
	y: number;
	angle: number;
}

/**
 * Online rollbacks (P10.4) move ships to their corrected position in one frame. This keeps a
 * drawing offset per ship (old drawn position minus corrected position) that fades away
 * quickly, so the ship glides into place instead of jumping. Rendering only.
 */
export class CorrectionSmoother {
	private readonly offsets = new Map<EntityId, PoseOffset>();
	private before = new Map<EntityId, { x: number; y: number; angle: number }>();

	/** Right before a rollback restores an earlier state. */
	capture(world: WorldState): void {
		this.before = new Map();
		for (const e of world.entities) {
			if (e.kind === "ship") this.before.set(e.id, { x: e.pos.x, y: e.pos.y, angle: e.angle });
		}
	}

	/** After the re-simulation: the jump each ship made becomes (or adds to) its offset. */
	corrected(world: WorldState): void {
		for (const e of world.entities) {
			if (e.kind !== "ship") continue;
			const old = this.before.get(e.id);
			if (!old) continue;
			const d = wrappedDelta(e.pos, old, world.size);
			const prev = this.offsets.get(e.id);
			const x = d.x + (prev?.x ?? 0);
			const y = d.y + (prev?.y ?? 0);
			if (Math.hypot(x, y) > CORRECTION_SNAP_DISTANCE) {
				this.offsets.delete(e.id);
				continue;
			}
			const angle = angleDelta(e.angle, old.angle) + (prev?.angle ?? 0);
			this.offsets.set(e.id, { x, y, angle });
		}
		this.before.clear();
	}

	/** Fades every offset; call once per frame. */
	decay(dtMs: number): void {
		const k = Math.exp(-Math.max(0, dtMs) / CORRECTION_FADE_MS);
		for (const [id, o] of this.offsets) {
			o.x *= k;
			o.y *= k;
			o.angle *= k;
			if (Math.abs(o.x) + Math.abs(o.y) < MIN_OFFSET && Math.abs(o.angle) < 1e-3) {
				this.offsets.delete(id);
			}
		}
	}

	offset(id: EntityId): PoseOffset | undefined {
		return this.offsets.get(id);
	}
}
